import React from "react";
import { Eye, MapPin } from "lucide-react";

import useDispatchGeotabCheck, { gapItemsOf, openDispatchGeotabGap, peekDispatchGeotabCheck, peekDispatchGeotabGap, subscribeDispatchGeotabCheck } from "../../hooks/useDispatchGeotabCheck.js";
import { concordText, countText, dayList, LIST_SECTIONS } from "../../utils/dispatchGeotabOrders.js";
import { HoverCard } from "../ResultsPrimitives.jsx";
import DispatchGeotabGapView from "./DispatchGeotabGapView.jsx";

// Liste Geotab d'une journée en cours ou passée, close ou non : l'épingle à droite de « Actions de journée » dès qu'il y a
// un écart, son survol, et la même fenêtre Geotab ouverte par l'épingle ou par « Clore la journée » (non bloquant, avec la
// question de fermeture et ses boutons). « Voir » passe au lecteur du camion.

const toCheckText = (list) => `${list.count} écart${list.count > 1 ? "s" : ""} à vérifier`;

function HoverList({ list }) {
  return (
    <div className="grid gap-1 text-[0.82rem] leading-5 text-slate-700">
      {LIST_SECTIONS.filter((section) => list[section.key].length).map((section) => (
        <div key={section.key}><b className="text-slate-900">{list[section.key].length}</b> · {section.title}</div>
      ))}
      {concordText(list) ? <div className="text-xs text-slate-500">{concordText(list)}.</div> : null}
      <div className="border-t border-slate-200 pt-1 text-xs text-slate-500">Clic : voir la liste.</div>
    </div>
  );
}

// La fenêtre Geotab, ouverte par l'épingle, « Clore la journée » ou le compteur d'une ligne du Gantt. Pour « Clore la
// journée », la fermer revient au Dispatch sans clore.
function DispatchGeotabGapHost({ date }) {
  const view = React.useSyncExternalStore(subscribeDispatchGeotabCheck, peekDispatchGeotabGap, () => null);
  const entry = view?.date === date ? peekDispatchGeotabCheck(date) : null;
  const items = gapItemsOf(entry?.data);
  if (!entry?.data?.eligible || (view.tab !== "orders" && !items.length)) return null;
  const closing = view.closing;
  const close = () => { openDispatchGeotabGap(date, null); closing?.cancel?.(); };
  return (
    <DispatchGeotabGapView data={entry.data} items={items} date={date} query={entry.query}
      index={Math.min(view.index, Math.max(items.length - 1, 0))} initialTab={view.tab}
      closing={closing && { title: closing.title, confirm: () => { openDispatchGeotabGap(date, null); closing.confirm(); } }}
      onIndex={(index) => openDispatchGeotabGap(date, index, "trucks", closing)} onClose={close} />
  );
}

export function DispatchGeotabCheckIcon({ date = "", revision = "", enabled = false, paused = false }) {
  const entry = useDispatchGeotabCheck({ date, revision, enabled, paused });
  const list = React.useMemo(() => dayList(entry?.data), [entry?.data]);
  const title = `Geotab · ${toCheckText(list)}`;
  return (
    <>
      <DispatchGeotabGapHost date={date} />
      {list.count ? <HoverCard title={title} placement="bottom-start" maxWidth={440} referenceAsChild content={<HoverList list={list} />}>
        <button
          type="button"
          aria-label={title}
          data-dispatch-geotab-check="true"
          onClick={() => openDispatchGeotabGap(date, 0, "orders")}
          className="inline-flex h-7 w-7 flex-none items-center justify-center self-center rounded-[4px] bg-[#3f5585] text-white animate-dispatch-source-changes-pulse motion-reduce:animate-none focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
        >
          <MapPin aria-hidden="true" className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </HoverCard> : null}
    </>
  );
}

const pendingOf = (entry) => Boolean(entry) && !entry.data && ["waiting", "loading"].includes(entry.status);

// « Clore la journée » ouvert pendant la lecture de Geotab : elle part tout de suite ; s'il y a des écarts, une ligne les
// annonce et « Voir la liste » ouvre la fenêtre Geotab de fermeture.
export function DispatchGeotabCheckSection({ date, closing }) {
  const entry = React.useSyncExternalStore(subscribeDispatchGeotabCheck, () => peekDispatchGeotabCheck(date), () => null);
  React.useEffect(() => { entry?.start?.(); }, [entry?.start]);
  const list = React.useMemo(() => dayList(entry?.data), [entry?.data]);
  if (!list.count) return pendingOf(entry) ? <p className="mt-2.5 text-[0.82rem] text-slate-500" role="status">Lecture de Geotab…</p> : null;
  return (
    <section aria-label={`Geotab · ${countText(list)}`} className="mt-2.5 flex flex-wrap items-center gap-2 rounded-[4px] border border-slate-300 bg-slate-50 px-2.5 py-1.5 text-[0.82rem] text-slate-700" data-dispatch-geotab-check-list="true">
      <img src="/brand/geotab-logo.svg" alt="Geotab" className="h-3" />
      <b className="text-slate-900">{countText(list)}</b>
      {concordText(list) ? <span className="text-slate-500">· {concordText(list)}</span> : null}
      <button type="button" data-dispatch-geotab-list-open="true" onClick={() => openDispatchGeotabGap(date, 0, "orders", closing)}
        className="ml-auto inline-flex items-center gap-1 whitespace-nowrap rounded-[4px] border border-[#3f5585] bg-[#3f5585] px-2 py-1 text-[0.78rem] font-bold text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent">
        <Eye aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.2} />Voir la liste
      </button>
    </section>
  );
}

// « Clore la journée » : avec des écarts Geotab, la fenêtre Geotab de la journée tient lieu de confirmation (rend null) ;
// pendant la lecture, la fenêtre habituelle l'attend ; sinon la fenêtre habituelle. closing : { confirm, cancel }.
export function withDispatchGeotabCheck(content, dispatchDate, closing) {
  const entry = peekDispatchGeotabCheck(dispatchDate);
  const withTitle = { title: content.title, ...closing };
  if (dayList(entry?.data).count) {
    openDispatchGeotabGap(dispatchDate, 0, "orders", withTitle);
    return null;
  }
  return pendingOf(entry) ? { ...content, children: <DispatchGeotabCheckSection date={dispatchDate} closing={withTitle} /> } : content;
}
